import { cloneElement } from 'react';
import PropTypes from 'prop-types';

import { Wrapper, IconWrapper, LabelAndBadgeWrapper, LabelWrapper } from './index.styles';

import Badge from '../badge';
import Icon from '../icon';

/**
 * Renders a navigation item. Supports icons and badges.
 */
export default function NavItem({
  as,
  badge,
  badgeColor = 'primary',
  badgeFaded,
  badgeSize = 'small',
  children,
  leftIconHeight = 16,
  leftIconSrc,
  leftIconWidth = 16,
  level,
  onClick,
  rightIconHeight = 16,
  rightIconSrc,
  rightIconWidth = 16,
  sub,
  ...otherProps
}) {
  return (
    <>
      <Wrapper {...otherProps} activeClassName="active" as={as} onClick={onClick} title={children}>
        {leftIconSrc && (
          <IconWrapper>
            <Icon alt="" src={leftIconSrc} width={leftIconWidth} height={leftIconHeight} inline />
          </IconWrapper>
        )}
        <LabelAndBadgeWrapper>
          <LabelWrapper>{children}</LabelWrapper>
          {badge && (
            <Badge color={badgeColor} faded={badgeFaded} size={badgeSize}>
              {badge}
            </Badge>
          )}
        </LabelAndBadgeWrapper>
        {rightIconSrc && (
          <IconWrapper>
            <Icon
              alt=""
              src={rightIconSrc}
              width={rightIconWidth}
              height={rightIconHeight}
              inline
            />
          </IconWrapper>
        )}
      </Wrapper>
      {sub && cloneElement(sub, { level: level + 1 })}
    </>
  );
}

NavItem.propTypes = {
  /**
   * Component that should render the nav item. A common use case would be to write `as={NavLink}`
   * to use it with React-Router.
   */
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  /**
   * Badge label.
   */
  badge: PropTypes.node,
  /**
   * Badge color.
   */
  badgeColor: PropTypes.oneOf(['blue', 'red', 'teal', 'coolGray']),
  /**
   * Whether badge background should be faded.
   */
  badgeFaded: PropTypes.bool,
  /**
   * Badge size.
   */
  badgeSize: PropTypes.oneOf(['regular', 'small']),
  /**
   * Nav item label.
   */
  children: PropTypes.node,
  /**
   * Nav item class name.
   */
  className: PropTypes.string,
  /**
   * Nav item href. Only to be used when nav item is rendered as a normal link.
   */
  href: PropTypes.string,
  /**
   * Left icon height in pixels (recommended if icon URL is used).
   */
  leftIconHeight: PropTypes.number,
  /**
   * Left icon, icon source or icon URL.
   */
  leftIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Left icon width in pixels (recommended if icon URL is used)
   */
  leftIconWidth: PropTypes.number,
  /**
   * Navigation level.
   */
  level: PropTypes.number,
  /**
   * Function to run on click.
   */
  onClick: PropTypes.func,
  /**
   * Right icon height in pixels (recommended if icon URL is used).
   */
  rightIconHeight: PropTypes.number,
  /**
   * Right icon, icon source or icon URL.
   */
  rightIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Right icon width in pixels (recommended if icon URL is used)
   */
  rightIconWidth: PropTypes.number,
  /**
   * Sub navigation content.
   */
  sub: PropTypes.node,
  /**
   * Nav item to. Only to be used when nav item is rendered as Link or NavLink.
   */
  to: PropTypes.string,
};
